import type {
  LayerServiceMetadata,
  LayerServiceSublayer,
} from "@/v2-backoffice-client/layer/service/layer.service.metadata"

/**
 * Deterministic prefill for the create flow: everything the probed service
 * metadata can answer without an AI call — identity texts, the feature code
 * slug, and a per-type starter Configuration.
 */

/** Transliterations for the characters PascalCase slugging would drop. */
const NORWEGIAN_TRANSLITERATIONS: Record<string, string> = {
  æ: "ae",
  ø: "o",
  å: "a",
}

/**
 * `MapLayer_NO_<PascalCase>` feature code from a service title, matching
 * the convention of every existing layer code.
 */
export function slugFeatureCode(title: string): string {
  const words = title
    .toLowerCase()
    .replaceAll(/[æøå]/g, (char) => NORWEGIAN_TRANSLITERATIONS[char] ?? char)
    .replaceAll(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))

  return words.length > 0 ? `MapLayer_NO_${words.join("_")}` : "MapLayer_NO_"
}

export type LayerCreateIdentityValue = {
  featureCode: string
  name: string
  description: string
}

/** Identity prefill from the probed service. */
export function deriveIdentity(
  metadata: LayerServiceMetadata,
): LayerCreateIdentityValue {
  return {
    featureCode: slugFeatureCode(metadata.title),
    name: metadata.title,
    description: metadata.description ?? "",
  }
}

/** Strips a trailing query string for configuration urls. */
function baseUrl(url: string) {
  return url.split(/[?#]/)[0].replace(/\/+$/, "")
}

function pickedSublayers(
  sublayers: LayerServiceSublayer[],
  visibleIds: ReadonlySet<number | string> | null,
) {
  if (!visibleIds) return sublayers
  return sublayers.filter((sublayer) => visibleIds.has(sublayer.id))
}

/**
 * The starter Configuration for a probed source, shaped per layer type the
 * way the console's layer factory expects it. `visibleIds` narrows group /
 * map-image / wms starters to the operator's sublayer picks (null = all).
 */
export function buildStarterConfiguration(
  metadata: LayerServiceMetadata,
  url: string,
  layerTypeCode: string,
  visibleIds: ReadonlySet<number | string> | null,
): Record<string, unknown> {
  const cleanUrl = baseUrl(url)

  if (layerTypeCode === "wms") {
    const sublayers = pickedSublayers(metadata.sublayers, visibleIds)
    return {
      url: cleanUrl,
      title: metadata.title,
      ...(sublayers.length > 0 && {
        sublayers: sublayers.map((sublayer) => ({
          name: sublayer.id,
          title: sublayer.name,
        })),
      }),
    }
  }

  if (layerTypeCode === "map-image") {
    const sublayers = pickedSublayers(metadata.sublayers, visibleIds)
    return {
      url: cleanUrl,
      title: metadata.title,
      // Only pin sublayers when the operator narrowed the selection;
      // otherwise the service's own default visibilities apply.
      ...(visibleIds &&
        sublayers.length > 0 && {
          sublayers: sublayers.map((sublayer) => ({
            id: sublayer.id,
            visible: true,
          })),
        }),
    }
  }

  if (layerTypeCode === "group") {
    const sublayers = pickedSublayers(metadata.sublayers, visibleIds)
    return {
      title: metadata.title,
      layers: sublayers.map((sublayer) => ({
        url: `${cleanUrl}/${sublayer.id}`,
        title: sublayer.name,
        visible: sublayer.defaultVisibility !== false,
      })),
    }
  }

  // feature / scene / anything single-endpoint: the probed url IS the layer.
  // A service root with exactly one sublayer gets the layerId instead of a
  // rewritten url, mirroring how existing configurations are written.
  if (
    !/\/\d+$/.test(cleanUrl) &&
    metadata.sublayers.length === 1 &&
    layerTypeCode === "feature"
  ) {
    return {
      url: cleanUrl,
      layerId: metadata.sublayers[0].id,
      title: metadata.title,
      outFields: ["*"],
    }
  }

  return {
    url: cleanUrl,
    title: metadata.title,
    ...(layerTypeCode === "feature" && { outFields: ["*"] }),
  }
}

/**
 * Applies a sublayer selection change to the CURRENT configuration draft
 * instead of re-seeding it, so manual and AI edits survive the toggle:
 * entries for still-selected sublayers stay exactly as the operator left
 * them, deselected ones drop out, newly selected ones get their starter
 * entry. Entries not matching any probed sublayer (hand-added) are never
 * touched. Returns null when the draft cannot be patched (invalid JSON or
 * a type without sublayers) — the caller falls back to a full re-seed.
 */
export function patchConfigurationSublayers(
  currentText: string,
  metadata: LayerServiceMetadata,
  url: string,
  layerTypeCode: string,
  visibleIds: ReadonlySet<number | string>,
): string | null {
  let configuration: Record<string, unknown>
  try {
    const parsed: unknown = JSON.parse(currentText)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null
    }
    configuration = parsed as Record<string, unknown>
  } catch {
    return null
  }

  const cleanUrl = baseUrl(url)
  const selected = pickedSublayers(metadata.sublayers, visibleIds)
  const existingEntries = (key: "layers" | "sublayers") =>
    Array.isArray(configuration[key])
      ? (configuration[key] as Record<string, unknown>[])
      : []

  if (layerTypeCode === "group") {
    const sublayerUrl = (id: number | string) => `${cleanUrl}/${id}`
    const kept = existingEntries("layers").filter((entry) => {
      const match = metadata.sublayers.find(
        (sublayer) => entry.url === sublayerUrl(sublayer.id),
      )
      return !match || visibleIds.has(match.id)
    })
    const keptUrls = new Set(kept.map((entry) => entry.url))
    const added = selected
      .filter((sublayer) => !keptUrls.has(sublayerUrl(sublayer.id)))
      .map((sublayer) => ({
        url: sublayerUrl(sublayer.id),
        title: sublayer.name,
        visible: sublayer.defaultVisibility !== false,
      }))
    return JSON.stringify(
      { ...configuration, layers: [...kept, ...added] },
      null,
      2,
    )
  }

  if (layerTypeCode === "map-image") {
    const byId = new Map(
      existingEntries("sublayers").map((entry) => [entry.id, entry]),
    )
    const sublayers = selected.map(
      (sublayer) => byId.get(sublayer.id) ?? { id: sublayer.id, visible: true },
    )
    return JSON.stringify({ ...configuration, sublayers }, null, 2)
  }

  if (layerTypeCode === "wms") {
    const byName = new Map(
      existingEntries("sublayers").map((entry) => [entry.name, entry]),
    )
    const sublayers = selected.map(
      (sublayer) =>
        byName.get(sublayer.id) ?? { name: sublayer.id, title: sublayer.name },
    )
    return JSON.stringify({ ...configuration, sublayers }, null, 2)
  }

  return null
}
